import React, { useState } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import {
    Card,
    Tabs,
    Button,
    Space,
    Tag,
    Typography,
    Empty,
    Row,
    Col,
    Statistic,
    Dropdown,
    Modal,
    Form,
    Select,
    message,
    Alert,
    Spin,
    InputNumber,
    Progress,
    Input,
    Switch,
    Tooltip,
} from 'antd';
import {
    ArrowLeftOutlined,
    ApartmentOutlined,
    CrownOutlined,
    UserOutlined,
    AppstoreOutlined,
    FileTextOutlined,
    HistoryOutlined,
    MoreOutlined,
    BarChartOutlined,
    UserSwitchOutlined,
    EditOutlined,
    MailOutlined,
    EyeOutlined,
    ThunderboltOutlined,
    ReloadOutlined,
    DeleteOutlined,
    ExclamationCircleOutlined,
    CreditCardOutlined,
    ShoppingOutlined,
    CheckCircleOutlined,
    ClockCircleOutlined,
    TeamOutlined,
} from '@ant-design/icons';
import {
    useGetAdminOrganizationDetailsQuery,
    useGetAdminOrganizationBrandsQuery,
    useGetAdminOrganizationSurveysQuery,
    useGetAdminOrganizationUsersQuery,
    useGetAdminOrganizationEventsQuery,
    useUpdateOrganizationStatusMutation,
    useUpdateOrganizationFeaturesMutation,
    useUpdateOrganizationBenchmarkStatusMutation,
    useUpdateOrganizationSuppressTrialEmailsMutation,
    useMoveOrganizationToCharterMutation,
    useGetAllMessagesQuery,
    useGetAdminTrialStatusQuery,
    useResetAdminFreeTrialMutation,
    useGetAdminOrganizationQuotaStatusQuery,
    useDeleteOrganizationMutation,
    useGetAdminOrganizationSubscriptionQuery,
    useGetAdminOrganizationAddonsQuery,
    useGetQualificationConfigQuery,
    useGetAdminAgenciesQuery,
    useAssignAdminOrganizationAgencyMutation,
    useRemoveAdminOrganizationAgencyMutation,
} from '../../services/nestjs-base-api';
import { businessModelLabel, productCategoryLabel } from '../../utils/qualificationLabels';
import { BrandKnowledgePanel } from '../ai-hub/BrandKnowledgePanel';
import ConciergePackPanel from '../components/concierge/ConciergePackPanel';
import { HypothesisRunnerPanel } from '../components/hypothesis/HypothesisRunnerPanel';
import { useAuth } from '../../hooks/useAuth';
import { ResponsiveTable } from '../components/ResponsiveTable';
import { ImpersonationModal } from '../components/ImpersonationModal';
import { OrganizationTokenUsageWidget } from '../components/OrganizationTokenUsageWidget';
import { OrganizationActivityTab } from '../components/OrganizationActivityTab';
import { EVENT_TYPE_COLORS, formatEventType } from '../utils/eventTypeUtils';
import { ColumnsType } from 'antd/es/table';
import { sanitizeTrustedHtml } from '../../utils/sanitize';
import dayjs from 'dayjs';
import relativeTime from 'dayjs/plugin/relativeTime';

dayjs.extend(relativeTime);

const { Title, Text } = Typography;
const { TabPane } = Tabs;

export const AdminOrganizationDetailsPage: React.FC = () => {
    const { id } = useParams<{ id: string }>();
    const { user } = useAuth();
    const navigate = useNavigate();
    const [activeTab, setActiveTab] = useState('users');
    const [isImpersonationModalVisible, setIsImpersonationModalVisible] = useState(false);
    const [isStatusModalVisible, setIsStatusModalVisible] = useState(false);
    const [isDeleteModalVisible, setIsDeleteModalVisible] = useState(false);
    const [isAgencyModalVisible, setIsAgencyModalVisible] = useState(false);
    const [isBenchmarkModalVisible, setIsBenchmarkModalVisible] = useState(false);
    const [deleteConfirmText, setDeleteConfirmText] = useState('');
    const [statusForm] = Form.useForm();
    const [benchmarkForm] = Form.useForm();
    const [quotaForm] = Form.useForm();
    const [agencyForm] = Form.useForm();

    const organizationId = parseInt(id || '0', 10);

    // API queries
    const { data: organization, isLoading: orgLoading, error: orgError, refetch: refetchOrg } = 
        useGetAdminOrganizationDetailsQuery({
            jwtToken: user?.jwtToken || '',
            id: organizationId,
        });

    const { data: brandsData, isLoading: brandsLoading } = useGetAdminOrganizationBrandsQuery({
        jwtToken: user?.jwtToken || '',
        organizationId,
    });

    // Human labels for the org's stored business_model / product_category.
    const { data: qualificationConfig } = useGetQualificationConfigQuery();

    const { data: surveysData, isLoading: surveysLoading } = useGetAdminOrganizationSurveysQuery({
        jwtToken: user?.jwtToken || '',
        organizationId,
    });

    const { data: usersData, isLoading: usersLoading } = useGetAdminOrganizationUsersQuery({
        jwtToken: user?.jwtToken || '',
        organizationId,
    });

    const { data: eventsData, isLoading: eventsLoading } = useGetAdminOrganizationEventsQuery({
        jwtToken: user?.jwtToken || '',
        organizationId,
        limit: 50,
    });

    const { data: quotaStatus, isLoading: quotaLoading, refetch: refetchQuota } = useGetAdminOrganizationQuotaStatusQuery({
        jwtToken: user?.jwtToken || '',
        orgId: organizationId,
    });

    const { data: subscriptionData, isLoading: subscriptionLoading } = useGetAdminOrganizationSubscriptionQuery({
        jwtToken: user?.jwtToken || '',
        organizationId,
    });

    const { data: addonsData, isLoading: addonsLoading } = useGetAdminOrganizationAddonsQuery({
        jwtToken: user?.jwtToken || '',
        organizationId,
    });

    const { data: agencies } = useGetAdminAgenciesQuery(
        { jwtToken: user?.jwtToken || '' },
        { skip: !isAgencyModalVisible },
    );

    // Mutations
    const [updateStatus] = useUpdateOrganizationStatusMutation();
    const [updateFeatures] = useUpdateOrganizationFeaturesMutation();
    const [updateBenchmarkStatus, { isLoading: isUpdatingBenchmarkStatus }] = useUpdateOrganizationBenchmarkStatusMutation();
    const [updateSuppressTrialEmails, { isLoading: isUpdatingSuppressTrialEmails }] = useUpdateOrganizationSuppressTrialEmailsMutation();
    const [moveToCharter, { isLoading: isMovingToCharter }] = useMoveOrganizationToCharterMutation();

    // Trial emails withheld from this org while it is being onboarded
    // personally. Held ones are stored as drafts, so filter on that and keep
    // only the concierge-suppressed ones (an admin's own half-written draft is
    // also 'draft' but carries no suppressed flag).
    const { data: heldMessagesData, isLoading: heldMessagesLoading } = useGetAllMessagesQuery({
        organisationId: organizationId,
        status: 'draft',
        limit: 100,
    });
    const heldMessages = (heldMessagesData?.messages || []).filter(
        (m: any) => m?.metadata?.suppressed,
    );
    const [viewingHeldEmail, setViewingHeldEmail] = useState<any | null>(null);
    const [resetFreeTrial] = useResetAdminFreeTrialMutation();

    // Where this org is up to in its trial. Returns null for orgs not on one.
    const { data: trialStatus } = useGetAdminTrialStatusQuery(
        { jwtToken: user?.jwtToken || '', orgId: organizationId },
        { skip: !organizationId },
    );

    // Only meaningful while a free trial is actually running.
    const isOnFreeTrial = organization?.subscription?.status === 'trialing';

    const handleResetTrial = () => {
        Modal.confirm({
            title: 'Reset free trial to day 0',
            icon: <ThunderboltOutlined style={{ color: '#1890ff' }} />,
            width: 560,
            content: (
                <div>
                    <p>
                        This restarts the trial clock for <strong>{organization?.name}</strong> — a
                        full-length trial and a fresh response-collection window, starting today.
                    </p>
                    <ul style={{ paddingLeft: 20, marginTop: 12 }}>
                        <li>Emails they have already been sent will <strong>not</strong> be sent again</li>
                        <li>Responses collected so far <strong>still count</strong> toward the requirement</li>
                        <li>If they had already met the response requirement, they stay qualified</li>
                        <li>No extra AI credits are allocated</li>
                    </ul>
                </div>
            ),
            okText: 'Reset trial',
            onOk: async () => {
                try {
                    await resetFreeTrial({
                        jwtToken: user?.jwtToken || '',
                        orgId: organizationId,
                    }).unwrap();
                    message.success('Free trial reset to day 0');
                    refetchOrg();
                } catch (error: any) {
                    message.error(error?.data?.message || 'Failed to reset free trial');
                }
            },
        });
    };
    const [deleteOrganization, { isLoading: isDeleting }] = useDeleteOrganizationMutation();
    const [assignAgency, { isLoading: isAssigningAgency }] = useAssignAdminOrganizationAgencyMutation();
    const [removeAgency] = useRemoveAdminOrganizationAgencyMutation();

    const handleStatusUpdate = async (values: { status: string }) => {
        try {
            await updateStatus({
                jwtToken: user?.jwtToken || '',
                id: organizationId,
                status: values.status,
            }).unwrap();
            message.success('Organization status updated successfully');
            setIsStatusModalVisible(false);
            refetchOrg();
        } catch (error) {
            message.error('Failed to update organization status');
        }
    };

    const handleBenchmarkStatusUpdate = async (values: { benchmarkStatus: 'pending' | 'included' | 'excluded' }) => {
        try {
            await updateBenchmarkStatus({
                jwtToken: user?.jwtToken || '',
                id: organizationId,
                benchmarkStatus: values.benchmarkStatus,
            }).unwrap();
            message.success('Benchmark status updated — recalculation started');
            setIsBenchmarkModalVisible(false);
            refetchOrg();
        } catch (error) {
            message.error('Failed to update benchmark status');
        }
    };

    // Concierge mode. Holds the automated trial emails as drafts; the trial
    // itself, including the deadline downgrade, is unaffected.
    const handleSuppressTrialEmailsToggle = async (suppressTrialEmails: boolean) => {
        try {
            const result = await updateSuppressTrialEmails({
                jwtToken: user?.jwtToken || '',
                id: organizationId,
                suppressTrialEmails,
            }).unwrap();
            message.success(result.message);
            refetchOrg();
        } catch (error) {
            message.error('Failed to update trial email handling');
        }
    };

    // Charter Cohort (SYR-106). A brand already on the platform who buys a
    // seat. Its own action rather than a plan dropdown, because changePlan
    // writes planId alone and would leave a monthly billing cycle, a stale
    // renewal date on the customer's own billing screen, and none of the
    // charter_* fields the week-nine readout depends on.
    const handleMoveToCharter = () => {
        Modal.confirm({
            title: 'Move to the Charter cohort',
            icon: <CrownOutlined style={{ color: '#faad14' }} />,
            content: (
                <div>
                    <p><strong>{organization?.name}</strong> moves onto a paid Charter seat.</p>
                    <ul style={{ paddingLeft: 20, marginTop: 12 }}>
                        <li>Charter plan, annual billing, renewing in a year</li>
                        <li>Any running trial ends immediately, without a downgrade</li>
                        <li>Trial emails are held from here on</li>
                        <li>First send committed within 30 days</li>
                    </ul>
                    <p style={{ marginTop: 12, marginBottom: 0 }}>Do this only once the invoice is paid.</p>
                </div>
            ),
            okText: 'Move to Charter',
            onOk: async () => {
                try {
                    const result = await moveToCharter({
                        jwtToken: user?.jwtToken || '',
                        id: organizationId,
                    }).unwrap();
                    message.success(result.message);
                    refetchOrg();
                } catch (error: any) {
                    message.error(error?.data?.message || 'Failed to move this organisation to Charter');
                }
            },
        });
    };

    const handleQuotaUpdate = async (values: { aiTokenQuotaMonthly?: number; aiTokenQuotaDaily?: number }) => {
        try {
            await updateFeatures({
                jwtToken: user?.jwtToken || '',
                id: organizationId,
                aiTokenQuotaMonthly: values.aiTokenQuotaMonthly,
                aiTokenQuotaDaily: values.aiTokenQuotaDaily,
            }).unwrap();
            message.success('AI quota limits updated successfully');
            refetchQuota();
        } catch (error) {
            message.error('Failed to update AI quota limits');
        }
    };

    const handleDelete = async () => {
        if (deleteConfirmText !== organization?.name) {
            message.error('Organization name does not match');
            return;
        }

        try {
            await deleteOrganization({
                jwtToken: user?.jwtToken || '',
                id: organizationId,
            }).unwrap();
            message.success('Organization deleted successfully');
            setIsDeleteModalVisible(false);
            navigate('/admin/organizations');
        } catch (error) {
            message.error('Failed to delete organization');
        }
    };

    const handleAgencyAssign = async (values: { agencyOrganisationId: number }) => {
        try {
            await assignAgency({
                jwtToken: user?.jwtToken || '',
                id: organizationId,
                agencyOrganisationId: values.agencyOrganisationId,
            }).unwrap();
            message.success('Organization assigned to agency');
            setIsAgencyModalVisible(false);
            agencyForm.resetFields();
            refetchOrg();
        } catch (error: any) {
            message.error(error?.data?.message || 'Failed to assign organization to agency');
        }
    };

    const handleAgencyRemove = () => {
        Modal.confirm({
            title: 'Remove from agency?',
            icon: <ExclamationCircleOutlined />,
            content: (
                <div>
                    <p>
                        This ends the relationship with <strong>{organization?.agency?.agencyName}</strong>.
                        Agency users lose access to this organization.
                    </p>
                    {organization?.agency?.billingResponsibility === 'AGENCY' && (
                        <p style={{ color: '#fa8c16' }}>
                            The agency currently pays for this organization — billing will be
                            released back to the client.
                        </p>
                    )}
                </div>
            ),
            okText: 'Remove',
            okButtonProps: { danger: true },
            onOk: async () => {
                try {
                    await removeAgency({
                        jwtToken: user?.jwtToken || '',
                        id: organizationId,
                    }).unwrap();
                    message.success('Organization removed from agency');
                    refetchOrg();
                } catch (error: any) {
                    message.error(error?.data?.message || 'Failed to remove organization from agency');
                }
            },
        });
    };

    const getStatusColor = (status: string) => {
        switch (status?.toUpperCase()) {
            case 'ACTIVE': return 'green';
            case 'INACTIVE': return 'red';
            case 'PENDING': return 'orange';
            case 'SUSPENDED': return 'red';
            default: return 'default';
        }
    };

    // More actions dropdown items
    const moreActions = [
        {
            key: 'impersonate',
            label: 'Impersonate User',
            icon: <UserSwitchOutlined />,
        },
        {
            key: 'status',
            label: 'Update Status',
            icon: <EditOutlined />,
        },
        {
            key: 'benchmark-status',
            label: 'Set Benchmark Status',
            icon: <BarChartOutlined />,
        },
        ...(organization?.orgType !== 'agency'
            ? [
                organization?.agency
                    ? {
                        key: 'remove-agency',
                        label: 'Remove from Agency',
                        icon: <TeamOutlined />,
                        danger: true,
                    }
                    : {
                        key: 'assign-agency',
                        label: 'Assign to Agency',
                        icon: <TeamOutlined />,
                    },
            ]
            : []),
        ...(isOnFreeTrial
            ? [
                {
                    key: 'reset-trial',
                    label: 'Reset Free Trial to Day 0',
                    icon: <ThunderboltOutlined />,
                },
            ]
            : []),
        {
            key: 'email',
            label: 'Send Notification',
            icon: <MailOutlined />,
            disabled: true,
        },
        {
            key: 'delete',
            label: 'Delete Organization',
            icon: <DeleteOutlined />,
            danger: true,
        },
    ];

    const handleMenuClick = ({ key }: { key: string }) => {
        switch (key) {
            case 'impersonate':
                setIsImpersonationModalVisible(true);
                break;
            case 'status':
                statusForm.setFieldsValue({ status: organization?.status });
                setIsStatusModalVisible(true);
                break;
            case 'benchmark-status':
                benchmarkForm.setFieldsValue({ benchmarkStatus: organization?.benchmarkStatus || 'pending' });
                setIsBenchmarkModalVisible(true);
                break;
            case 'assign-agency':
                setIsAgencyModalVisible(true);
                break;
            case 'reset-trial':
                handleResetTrial();
                break;
            case 'remove-agency':
                handleAgencyRemove();
                break;
            case 'delete':
                setDeleteConfirmText('');
                setIsDeleteModalVisible(true);
                break;
        }
    };

    // Table columns for different tabs
    const usersColumns: ColumnsType<any> = [
        {
            title: 'Name',
            key: 'name',
            render: (_, record) => (
                <div>
                    <div className="font-medium">
                        {record.firstName} {record.lastName}
                    </div>
                    <div className="text-sm text-gray-500">{record.email}</div>
                </div>
            ),
        },
        {
            title: 'Role',
            dataIndex: 'roles',
            key: 'roles',
            render: (roles: string | string[]) => {
                const roleArray = typeof roles === 'string' ? JSON.parse(roles || '[]') : roles;
                return roleArray.map((role: string) => (
                    <Tag key={role} color="blue">
                        {role}
                    </Tag>
                ));
            },
        },
        {
            title: 'Status',
            dataIndex: 'status',
            key: 'status',
            render: (status: string) => (
                <Tag color={getStatusColor(status)}>
                    {status?.toUpperCase() || 'UNKNOWN'}
                </Tag>
            ),
        },
        {
            title: 'Created',
            dataIndex: 'createdAt',
            key: 'createdAt',
            render: (date: string) => date ? dayjs(date).format('MMM DD, YYYY') : 'N/A',
        },
        {
            title: 'Actions',
            key: 'actions',
            render: () => (
                <Space>
                    <Button type="link" size="small" icon={<EyeOutlined />}>
                        View
                    </Button>
                    <Button type="link" size="small" icon={<EditOutlined />}>
                        Edit
                    </Button>
                </Space>
            ),
        },
    ];

    const brandsColumns: ColumnsType<any> = [
        {
            title: 'Brand',
            key: 'brand',
            render: (_, record) => (
                <div className="flex items-center space-x-3">
                    <div className="w-8 h-8 bg-gray-200 rounded-full flex items-center justify-center">
                        <AppstoreOutlined className="text-gray-500" />
                    </div>
                    <div>
                        <div className="font-medium">{record.name}</div>
                        <div className="text-sm text-gray-500">ID: {record.id}</div>
                    </div>
                </div>
            ),
        },
        {
            title: 'Status',
            dataIndex: 'status',
            key: 'status',
            render: (status: string) => (
                <Tag color={getStatusColor(status)}>
                    {status?.toUpperCase() || 'ACTIVE'}
                </Tag>
            ),
        },
        {
            title: 'Surveys',
            dataIndex: 'surveyCount',
            key: 'surveyCount',
            render: (count: number) => count || 0,
        },
        {
            title: 'Created',
            dataIndex: 'createdAt',
            key: 'createdAt',
            render: (date: string) => date ? dayjs(date).format('MMM DD, YYYY') : 'N/A',
        },
        {
            title: 'Actions',
            key: 'actions',
            render: (_, record) => (
                <Space>
                    <Link to={`/admin/brands/${record.id}/brand-equity`}>
                        <Button type="link" size="small" icon={<EyeOutlined />}>
                            Analytics
                        </Button>
                    </Link>
                </Space>
            ),
        },
    ];

    const surveysColumns: ColumnsType<any> = [
        {
            title: 'Survey',
            key: 'survey',
            render: (_, record) => (
                <div>
                    <div className="font-medium">{record.name}</div>
                    <div className="text-sm text-gray-500">{record.brand?.name}</div>
                </div>
            ),
        },
        {
            title: 'Status',
            dataIndex: 'status',
            key: 'status',
            render: (status: string) => (
                <Tag color={status === 'PUBLISHED' ? 'green' : status === 'DRAFT' ? 'orange' : 'default'}>
                    {status?.toUpperCase() || 'DRAFT'}
                </Tag>
            ),
        },
        {
            title: 'Responses',
            dataIndex: 'responseCount',
            key: 'responseCount',
            render: (count: number) => count || 0,
        },
        {
            title: 'Created',
            dataIndex: 'createdAt',
            key: 'createdAt',
            render: (date: string) => date ? dayjs(date).format('MMM DD, YYYY') : 'N/A',
        },
        {
            title: 'Actions',
            key: 'actions',
            render: (_, record) => (
                <Space>
                    <Link to={`/admin/surveys/${record.hash}`}>
                        <Button type="link" size="small" icon={<EyeOutlined />}>
                            View
                        </Button>
                    </Link>
                </Space>
            ),
        },
    ];

    const eventsColumns: ColumnsType<any> = [
        {
            title: 'Time',
            dataIndex: 'createdAt',
            key: 'createdAt',
            width: 120,
            render: (date: string) => (
                <div>
                    <div className="text-sm">{dayjs(date).format('MMM DD')}</div>
                    <div className="text-xs text-gray-500">{dayjs(date).format('HH:mm')}</div>
                </div>
            ),
        },
        {
            title: 'Event',
            dataIndex: 'eventType',
            key: 'eventType',
            render: (eventType: string) => (
                <Tag color={EVENT_TYPE_COLORS[eventType] || 'default'}>
                    {formatEventType(eventType)}
                </Tag>
            ),
        },
        {
            title: 'User',
            key: 'user',
            render: (_, record) => record.user?.email || 'System',
        },
    ];

    // notificationKey -> something readable. Falls back to the raw key so a
    // newly added trial email still lists rather than showing a blank cell.
    const HELD_EMAIL_LABELS: Record<string, string> = {
        trial_welcome: 'Trial welcome',
        response_progress: 'Response progress nudge',
        response_requirement_met: 'Response target met',
        response_requirement_not_met: 'Response target missed',
        response_deadline_warning: 'Response deadline warning',
        trial_ending_reminder: 'Trial ending reminder',
        trial_final_week: 'Trial final week',
        trial_expired: 'Trial expired',
    };

    const heldEmailColumns: ColumnsType<any> = [
        {
            title: 'Would have been sent',
            key: 'wouldHaveSentAt',
            width: 140,
            render: (_, record) => {
                const when = record?.metadata?.wouldHaveSentAt || record?.createdAt;
                return (
                    <div>
                        <div className="text-sm">{dayjs(when).format('MMM DD')}</div>
                        <div className="text-xs text-gray-500">{dayjs(when).format('HH:mm')}</div>
                    </div>
                );
            },
        },
        {
            title: 'Email',
            key: 'notificationKey',
            render: (_, record) => {
                const key = record?.metadata?.notificationKey;
                return <Tag color="orange">{HELD_EMAIL_LABELS[key] || key || 'Trial email'}</Tag>;
            },
        },
        {
            title: 'Subject',
            dataIndex: 'subject',
            key: 'subject',
        },
        {
            title: '',
            key: 'actions',
            width: 90,
            render: (_, record) => (
                <Button size="small" onClick={() => setViewingHeldEmail(record)}>
                    View
                </Button>
            ),
        },
    ];

    if (orgLoading) {
        return (
            <div style={{ padding: '24px', textAlign: 'center' }}>
                <Spin size="large" />
            </div>
        );
    }

    if (orgError || !organization) {
        return (
            <div style={{ padding: '24px' }}>
                <Alert
                    message="Error loading organization"
                    description="Organization not found or you don't have permission to view it."
                    type="error"
                    showIcon
                />
            </div>
        );
    }

    return (
        <div style={{ padding: '24px' }}>
            {/* Header */}
            <div style={{ marginBottom: '32px' }}>
                <div className="flex items-center justify-between">
                    <div className="flex items-center space-x-4">
                        <Link to="/admin/organizations">
                            <Button icon={<ArrowLeftOutlined />} type="text">
                                Back to Organizations
                            </Button>
                        </Link>
                        <div>
                            <Title level={2} className="!mb-0">
                                <ApartmentOutlined className="mr-3" />
                                {organization.name}
                            </Title>
                            <div style={{ marginTop: '12px' }}>
                                <Space>
                                    <Tag color={getStatusColor(organization.status || '')}>
                                        {organization.status?.toUpperCase() || 'UNKNOWN'}
                                    </Tag>
                                    {organization.orgType === 'agency' && (
                                        <Tag color="purple" icon={<TeamOutlined />}>
                                            AGENCY
                                        </Tag>
                                    )}
                                    {organization.agency && (
                                        <Tag color="geekblue" icon={<TeamOutlined />}>
                                            Managed by {organization.agency.agencyName}
                                            {organization.agency.status !== 'ACTIVE' ? ` (${organization.agency.status})` : ''}
                                        </Tag>
                                    )}
                                    <Text type="secondary">
                                        Created {dayjs(organization.createdAt).format('MMM DD, YYYY')}
                                    </Text>
                                </Space>
                            </div>
                        </div>
                    </div>
                    <Dropdown menu={{ items: moreActions, onClick: handleMenuClick }} placement="bottomRight">
                        <Button icon={<MoreOutlined />}>
                            Actions
                        </Button>
                    </Dropdown>
                </div>
            </div>

            {/* Overview Cards */}
            <Row gutter={[16, 16]} style={{ marginBottom: '24px' }}>
                <Col xs={24} sm={12} md={6}>
                    <Card>
                        <Statistic
                            title="Users"
                            value={organization.userCount || 0}
                            prefix={<UserOutlined />}
                            valueStyle={{ color: '#F59E0B' }}
                        />
                    </Card>
                </Col>
                <Col xs={24} sm={12} md={6}>
                    <Card>
                        <Statistic
                            title="Brands"
                            value={organization.brandCount || 0}
                            prefix={<AppstoreOutlined />}
                            valueStyle={{ color: '#52c41a' }}
                        />
                    </Card>
                </Col>
                <Col xs={24} sm={12} md={6}>
                    <Card>
                        <Statistic
                            title="Surveys"
                            value={organization.surveyCount || 0}
                            prefix={<FileTextOutlined />}
                            valueStyle={{ color: '#722ed1' }}
                        />
                    </Card>
                </Col>
                <Col xs={24} sm={12} md={6}>
                    <Card>
                        <Statistic
                            title="Total Responses"
                            value={organization.responseCount || 0}
                            prefix={<HistoryOutlined />}
                            valueStyle={{ color: '#eb2f96' }}
                        />
                    </Card>
                </Col>
            </Row>

            {/* Free trial progress — where the org actually is, at a glance:
                how far through the trial, and how the response requirement that
                keeps it alive is tracking. */}
            {trialStatus?.isOnFreeTrial && (
                <Card
                    title={
                        <span>
                            <ThunderboltOutlined style={{ marginRight: 8 }} />
                            Free Trial Progress
                        </span>
                    }
                    style={{ marginBottom: '24px' }}
                    extra={
                        <Tag color={
                            trialStatus.phase === 'expired'
                                ? 'red'
                                : trialStatus.phase === 'response_collection'
                                    ? 'orange'
                                    : 'green'
                        }>
                            {trialStatus.phase === 'response_collection'
                                ? 'Collecting responses'
                                : trialStatus.phase === 'expired'
                                    ? 'Expired'
                                    : 'Active trial'}
                        </Tag>
                    }
                >
                    <Row gutter={[24, 16]}>
                        <Col xs={24} md={12}>
                            <Text type="secondary" style={{ fontSize: 12 }}>TRIAL PERIOD</Text>
                            <div style={{ marginTop: 4, marginBottom: 8 }}>
                                <Text strong style={{ fontSize: 18 }}>
                                    Day {Math.max(0, trialStatus.totalDays - trialStatus.daysRemaining)} of {trialStatus.totalDays}
                                </Text>
                                <Text type="secondary" style={{ marginLeft: 8 }}>
                                    {trialStatus.daysRemaining} days left
                                </Text>
                            </div>
                            <Progress
                                percent={
                                    trialStatus.totalDays > 0
                                        ? Math.round(
                                            ((trialStatus.totalDays - trialStatus.daysRemaining) /
                                                trialStatus.totalDays) * 100,
                                        )
                                        : 0
                                }
                                showInfo={false}
                                strokeColor="#1890ff"
                            />
                            <div style={{ marginTop: 8 }}>
                                <Text type="secondary" style={{ fontSize: 12 }}>
                                    {dayjs(trialStatus.trialStart).format('MMM DD, YYYY')} → {dayjs(trialStatus.trialEnd).format('MMM DD, YYYY')}
                                </Text>
                            </div>
                        </Col>
                        <Col xs={24} md={12}>
                            <Text type="secondary" style={{ fontSize: 12 }}>RESPONSE REQUIREMENT</Text>
                            <div style={{ marginTop: 4, marginBottom: 8 }}>
                                <Text strong style={{ fontSize: 18 }}>
                                    {trialStatus.responseRequirement.collected} of {trialStatus.responseRequirement.required} responses
                                </Text>
                                {trialStatus.responseRequirement.met ? (
                                    <Tag color="green" style={{ marginLeft: 8 }}>Met</Tag>
                                ) : (
                                    <Tag color="orange" style={{ marginLeft: 8 }}>
                                        {trialStatus.responseRequirement.daysRemainingForResponses} days to go
                                    </Tag>
                                )}
                            </div>
                            <Progress
                                percent={
                                    trialStatus.responseRequirement.required > 0
                                        ? Math.min(100, Math.round(
                                            (trialStatus.responseRequirement.collected /
                                                trialStatus.responseRequirement.required) * 100,
                                        ))
                                        : 0
                                }
                                showInfo={false}
                                strokeColor={trialStatus.responseRequirement.met ? '#52c41a' : '#faad14'}
                            />
                            <div style={{ marginTop: 8 }}>
                                <Text type="secondary" style={{ fontSize: 12 }}>
                                    {trialStatus.responseRequirement.met
                                        ? 'Requirement met — the trial runs to its end date'
                                        : `Deadline ${dayjs(trialStatus.responseRequirement.deadline).format('MMM DD, YYYY')} — missing it downgrades the org to Free`}
                                </Text>
                            </div>
                        </Col>
                    </Row>
                </Card>
            )}

            {/* Qualification / Template Configuration */}
            <Card title="Configuration" style={{ marginBottom: '24px' }}>
                <Row gutter={[16, 16]}>
                    <Col xs={24} sm={12} md={6}>
                        <Text type="secondary" style={{ fontSize: 12 }}>BUSINESS MODEL</Text>
                        <div style={{ marginTop: 4 }}>
                            <Text strong>
                                {businessModelLabel(qualificationConfig, organization.businessModel) || '—'}
                            </Text>
                        </div>
                    </Col>
                    <Col xs={24} sm={12} md={6}>
                        <Text type="secondary" style={{ fontSize: 12 }}>PRODUCT CATEGORY</Text>
                        <div style={{ marginTop: 4 }}>
                            <Text strong>
                                {productCategoryLabel(qualificationConfig, organization.productCategory) || '—'}
                            </Text>
                        </div>
                    </Col>
                    <Col xs={24} sm={12} md={6}>
                        <Text type="secondary" style={{ fontSize: 12 }}>TEMPLATE TYPE</Text>
                        <div style={{ marginTop: 4 }}>
                            <Text strong>{organization.templateType ? <Tag>{organization.templateType}</Tag> : '—'}</Text>
                        </div>
                    </Col>
                    <Col xs={24} sm={12} md={6}>
                        <Text type="secondary" style={{ fontSize: 12 }}>PRODUCT TYPE</Text>
                        <div style={{ marginTop: 4 }}>
                            <Text strong>{organization.productTypeOverride ? <Tag>{organization.productTypeOverride}</Tag> : '—'}</Text>
                        </div>
                    </Col>
                    <Col xs={24} sm={12} md={6}>
                        <Text type="secondary" style={{ fontSize: 12 }}>BENCHMARK POOL</Text>
                        <div style={{ marginTop: 4 }}>
                            {organization.benchmarkStatus === 'included' ? (
                                <Tag color="green">Included</Tag>
                            ) : organization.benchmarkStatus === 'excluded' ? (
                                <Tag color="default">Excluded</Tag>
                            ) : (
                                <Tag color="orange">Pending review</Tag>
                            )}
                        </div>
                    </Col>
                    <Col xs={24} sm={12} md={6}>
                        <Text type="secondary" style={{ fontSize: 12 }}>CHARTER</Text>
                        <div style={{ marginTop: 4 }}>
                            {organization.charterJoinedAt ? (
                                <Tooltip title={`Joined ${dayjs(organization.charterJoinedAt).format('D MMM YYYY')}. First send committed by ${organization.charterSendCommitmentEndsAt ? dayjs(organization.charterSendCommitmentEndsAt).format('D MMM YYYY') : 'unknown'}.`}>
                                    <Tag color="gold">Charter member</Tag>
                                </Tooltip>
                            ) : (
                                <Button size="small" onClick={handleMoveToCharter} loading={isMovingToCharter}>
                                    Move to Charter
                                </Button>
                            )}
                        </div>
                    </Col>
                    <Col xs={24} sm={12} md={6}>
                        <Text type="secondary" style={{ fontSize: 12 }}>TRIAL EMAILS</Text>
                        <div style={{ marginTop: 4 }}>
                            <Space size={8}>
                                <Switch
                                    size="small"
                                    checked={!!organization.suppressTrialEmails}
                                    loading={isUpdatingSuppressTrialEmails}
                                    onChange={handleSuppressTrialEmailsToggle}
                                />
                                {organization.suppressTrialEmails ? (
                                    <Tooltip title="Automated trial emails are held as drafts so you can send something personally. The trial itself, including the deadline downgrade, still runs. Read the held emails under Messages.">
                                        <Tag color="orange">Held (concierge)</Tag>
                                    </Tooltip>
                                ) : (
                                    <Tag color="green">Sending</Tag>
                                )}
                            </Space>
                        </div>
                    </Col>
                    <Col xs={24} sm={12} md={6}>
                        <Text type="secondary" style={{ fontSize: 12 }}>AGENCY</Text>
                        <div style={{ marginTop: 4 }}>
                            {organization.orgType === 'agency' ? (
                                <Tag color="purple">Agency account</Tag>
                            ) : organization.agency ? (
                                <Space size={4} wrap>
                                    <Link to={`/admin/organizations/${organization.agency.agencyOrganisationId}`}>
                                        <Text strong style={{ color: '#1677ff' }}>
                                            {organization.agency.agencyName}
                                        </Text>
                                    </Link>
                                    <Tag color={organization.agency.status === 'ACTIVE' ? 'green' : 'orange'}>
                                        {organization.agency.status}
                                    </Tag>
                                    {organization.agency.billingResponsibility === 'AGENCY' && (
                                        <Tag color="gold">Agency-billed</Tag>
                                    )}
                                </Space>
                            ) : (
                                <Text strong>—</Text>
                            )}
                        </div>
                    </Col>
                </Row>
            </Card>

            {/* Tabs Content */}
            <Card>
                <Tabs activeKey={activeTab} onChange={setActiveTab}>
                    <TabPane tab="Users" key="users">
                        <ResponsiveTable
                            columns={usersColumns}
                            dataSource={usersData?.data || []}
                            loading={usersLoading}
                            rowKey="id"
                            pagination={{ pageSize: 50 }}
                        />
                    </TabPane>
                    <TabPane tab="Brands" key="brands">
                        <ResponsiveTable
                            columns={brandsColumns}
                            dataSource={brandsData?.data || []}
                            loading={brandsLoading}
                            rowKey="id"
                            pagination={{ pageSize: 50 }}
                        />
                    </TabPane>
                    <TabPane tab="Brand Knowledge" key="brand-knowledge">
                        {(brandsData?.data || []).length === 0 ? (
                            <Empty description="This organisation has no brands yet." />
                        ) : (
                            (brandsData?.data || []).map((brand: any) => (
                                <div key={brand.id} style={{ marginBottom: 32 }}>
                                    {(brandsData?.data || []).length > 1 && (
                                        <Title level={4}>{brand.name}</Title>
                                    )}
                                    <BrandKnowledgePanel
                                        brandId={brand.id}
                                        showHeader={false}
                                    />
                                </div>
                            ))
                        )}
                    </TabPane>
                    {/* SYR-90 Phase 6A — concierge cockpit scoped to this org's
                        brands (auto-selects when there is only one). */}
                    <TabPane tab="The Hypothesis" key="hypothesis">
                        <HypothesisRunnerPanel
                            jwt={user?.jwtToken || ''}
                            organisationId={organizationId}
                        />
                    </TabPane>
                    {/* SYR-105 — this org's concierge pack; review and approval
                        live on the Concierge queue itself. */}
                    <TabPane tab="Concierge" key="concierge">
                        <ConciergePackPanel
                            jwt={user?.jwtToken || ''}
                            organisationId={organizationId}
                            websiteUrl={organization?.websiteUrl ?? null}
                            onWebsiteUrlSaved={refetchOrg}
                        />
                    </TabPane>
                    <TabPane tab="Surveys" key="surveys">
                        <ResponsiveTable
                            columns={surveysColumns}
                            dataSource={surveysData?.data || []}
                            loading={surveysLoading}
                            rowKey="id"
                            pagination={{ pageSize: 50 }}
                        />
                    </TabPane>
                    <TabPane tab="Activity" key="activity">
                        <ResponsiveTable
                            columns={eventsColumns}
                            dataSource={eventsData?.events || []}
                            loading={eventsLoading}
                            rowKey="id"
                            pagination={{ pageSize: 50 }}
                            size="small"
                        />
                    </TabPane>
                    {/* Distinct from "Activity" above: that is our own platform
                        event log (server-side actions); this is the PostHog
                        clickstream — what they actually clicked in the UI. */}
                    <TabPane tab="User Journey" key="user-journey">
                        <OrganizationActivityTab
                            jwtToken={user?.jwtToken || ''}
                            organisationId={organizationId}
                        />
                    </TabPane>
                    {/* Only worth a tab for concierged orgs — but keep it while
                        held mail exists, so the history stays reachable after
                        the flag is switched back off. */}
                    {(organization.suppressTrialEmails || heldMessages.length > 0) && (
                        <TabPane
                            tab={
                                <span>
                                    <MailOutlined style={{ marginRight: 8 }} />
                                    Held Emails
                                    {heldMessages.length > 0 && (
                                        <Tag color="orange" style={{ marginLeft: 8 }}>
                                            {heldMessages.length}
                                        </Tag>
                                    )}
                                </span>
                            }
                            key="held-emails"
                        >
                            <Alert
                                message="Trial emails held for personal follow-up"
                                description="These were generated but never delivered, because this organisation is being onboarded personally. The trial itself still runs on schedule — including the downgrade if the response target is missed — so anything time-critical here needs sending by hand."
                                type="info"
                                showIcon
                                style={{ marginBottom: 16 }}
                            />
                            <ResponsiveTable
                                columns={heldEmailColumns}
                                dataSource={heldMessages}
                                loading={heldMessagesLoading}
                                rowKey="id"
                                pagination={{ pageSize: 20 }}
                                size="small"
                                locale={{ emptyText: 'No emails have been held yet' }}
                            />
                        </TabPane>
                    )}
                    <TabPane tab="AI Token Usage" key="token-usage">
                        <OrganizationTokenUsageWidget organizationId={organizationId} days={30} />
                    </TabPane>
                    <TabPane
                        tab={
                            <span>
                                <ThunderboltOutlined style={{ marginRight: 8 }} />
                                AI Quota
                            </span>
                        }
                        key="ai-quota"
                    >
                        {quotaLoading ? (
                            <div style={{ textAlign: 'center', padding: '40px' }}>
                                <Spin size="large" />
                            </div>
                        ) : quotaStatus ? (
                            <>
                                {/* Current Quota Status */}
                                <Alert
                                    message="AI Token Quota Management"
                                    description="Configure custom AI token quotas for this organization. Leave fields empty to use tier-based defaults."
                                    type="info"
                                    showIcon
                                    icon={<ThunderboltOutlined />}
                                    style={{ marginBottom: 24 }}
                                />

                                {!quotaStatus.enforcementEnabled && (
                                    <Alert
                                        message="Quota Enforcement Disabled"
                                        description="AI usage tracking is active but quotas are not enforced. You can monitor token consumption without limits."
                                        type="info"
                                        showIcon
                                        style={{ marginBottom: 16 }}
                                    />
                                )}

                                {/* Current Usage */}
                                <Card
                                    title="Current Usage"
                                    style={{ marginBottom: 24 }}
                                    extra={
                                        <Button
                                            icon={<ReloadOutlined />}
                                            onClick={() => refetchQuota()}
                                            loading={quotaLoading}
                                            size="small"
                                        >
                                            Refresh
                                        </Button>
                                    }
                                >
                                    <Row gutter={[16, 16]}>
                                        <Col xs={24} md={12}>
                                            <Card size="small">
                                                <Statistic
                                                    title="Daily Usage"
                                                    value={quotaStatus.enforcementEnabled ? quotaStatus.daily.percentageUsed : quotaStatus.daily.usage}
                                                    suffix={quotaStatus.enforcementEnabled ? '%' : ' tokens'}
                                                    valueStyle={{
                                                        color: quotaStatus.enforcementEnabled ? (
                                                            quotaStatus.daily.percentageUsed > 90 ? '#cf1322' :
                                                            quotaStatus.daily.percentageUsed > 80 ? '#fa8c16' : '#3f8600'
                                                        ) : '#1890ff'
                                                    }}
                                                />
                                                <Progress
                                                    percent={quotaStatus.enforcementEnabled ? Math.round(quotaStatus.daily.percentageUsed) : 0}
                                                    strokeColor={quotaStatus.enforcementEnabled ? (
                                                        quotaStatus.daily.percentageUsed > 90 ? '#cf1322' :
                                                        quotaStatus.daily.percentageUsed > 80 ? '#fa8c16' : '#3f8600'
                                                    ) : '#1890ff'}
                                                    style={{ marginTop: 8 }}
                                                />
                                                <div style={{ marginTop: 8, fontSize: '12px', color: '#666' }}>
                                                    {quotaStatus.daily.usage.toLocaleString()} {quotaStatus.enforcementEnabled ? `/ ${quotaStatus.daily.limit.toLocaleString()}` : '(no limit)'} tokens
                                                </div>
                                            </Card>
                                        </Col>
                                        <Col xs={24} md={12}>
                                            <Card size="small">
                                                <Statistic
                                                    title="Monthly Usage"
                                                    value={quotaStatus.enforcementEnabled ? quotaStatus.monthly.percentageUsed : quotaStatus.monthly.usage}
                                                    suffix={quotaStatus.enforcementEnabled ? '%' : ' tokens'}
                                                    valueStyle={{
                                                        color: quotaStatus.enforcementEnabled ? (
                                                            quotaStatus.monthly.percentageUsed > 90 ? '#cf1322' :
                                                            quotaStatus.monthly.percentageUsed > 80 ? '#fa8c16' : '#3f8600'
                                                        ) : '#1890ff'
                                                    }}
                                                />
                                                <Progress
                                                    percent={quotaStatus.enforcementEnabled ? Math.round(quotaStatus.monthly.percentageUsed) : 0}
                                                    strokeColor={quotaStatus.enforcementEnabled ? (
                                                        quotaStatus.monthly.percentageUsed > 90 ? '#cf1322' :
                                                        quotaStatus.monthly.percentageUsed > 80 ? '#fa8c16' : '#3f8600'
                                                    ) : '#1890ff'}
                                                    style={{ marginTop: 8 }}
                                                />
                                                <div style={{ marginTop: 8, fontSize: '12px', color: '#666' }}>
                                                    {quotaStatus.monthly.usage.toLocaleString()} {quotaStatus.enforcementEnabled ? `/ ${quotaStatus.monthly.limit.toLocaleString()}` : '(no limit)'} tokens
                                                </div>
                                            </Card>
                                        </Col>
                                    </Row>
                                </Card>

                                {/* Quota Configuration */}
                                <Card title="Custom Quota Limits">
                                    <Form
                                        form={quotaForm}
                                        layout="vertical"
                                        onFinish={handleQuotaUpdate}
                                    >
                                        <Alert
                                            message="Override Tier-Based Defaults"
                                            description="Set custom quota limits to override the tier-based defaults. Leave blank to use default values based on the organization's subscription tier."
                                            type="warning"
                                            showIcon
                                            style={{ marginBottom: 24 }}
                                        />

                                        <Row gutter={16}>
                                            <Col xs={24} md={12}>
                                                <Form.Item
                                                    name="aiTokenQuotaMonthly"
                                                    label="Monthly Token Quota (tokens)"
                                                    extra={`Default: ${quotaStatus.monthly.limit.toLocaleString()} tokens`}
                                                >
                                                    <InputNumber
                                                        min={0}
                                                        style={{ width: '100%' }}
                                                        placeholder="Leave empty for default"
                                                        formatter={value => value ? `${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ',') : ''}
                                                    />
                                                </Form.Item>
                                            </Col>
                                            <Col xs={24} md={12}>
                                                <Form.Item
                                                    name="aiTokenQuotaDaily"
                                                    label="Daily Token Quota (tokens)"
                                                    extra={`Default: ${quotaStatus.daily.limit.toLocaleString()} tokens`}
                                                >
                                                    <InputNumber
                                                        min={0}
                                                        style={{ width: '100%' }}
                                                        placeholder="Leave empty for default"
                                                        formatter={value => value ? `${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ',') : ''}
                                                    />
                                                </Form.Item>
                                            </Col>
                                        </Row>

                                        <Form.Item>
                                            <Button type="primary" htmlType="submit" icon={<ThunderboltOutlined style={{ marginRight: 4 }} />}>
                                                Update Quota Limits
                                            </Button>
                                        </Form.Item>
                                    </Form>
                                </Card>
                            </>
                        ) : (
                            <Alert
                                message="Quota information unavailable"
                                description="Unable to load AI quota information for this organization."
                                type="warning"
                                showIcon
                            />
                        )}
                    </TabPane>
                    <TabPane
                        tab={
                            <span>
                                <CreditCardOutlined style={{ marginRight: 8 }} />
                                Subscription & Add-ons
                            </span>
                        }
                        key="subscription"
                    >
                        {subscriptionLoading || addonsLoading ? (
                            <div style={{ textAlign: 'center', padding: '40px' }}>
                                <Spin size="large" />
                            </div>
                        ) : (
                            <>
                                {/* Subscription Info */}
                                <Card title="Current Subscription" style={{ marginBottom: 24 }}>
                                    {subscriptionData ? (
                                        <Row gutter={[16, 16]}>
                                            <Col xs={24} md={8}>
                                                <Statistic
                                                    title="Plan"
                                                    value={subscriptionData.plan?.name || subscriptionData.planId || 'No Plan'}
                                                    valueStyle={{ color: '#F59E0B' }}
                                                />
                                            </Col>
                                            <Col xs={24} md={8}>
                                                <Statistic
                                                    title="Status"
                                                    value={subscriptionData.status?.toUpperCase() || 'N/A'}
                                                    valueStyle={{
                                                        color: subscriptionData.status === 'active' ? '#52c41a' :
                                                               subscriptionData.status === 'trialing' ? '#fa8c16' : '#ff4d4f'
                                                    }}
                                                />
                                            </Col>
                                            <Col xs={24} md={8}>
                                                <div style={{ marginBottom: 8 }}>
                                                    <Text type="secondary">Stripe Customer ID</Text>
                                                </div>
                                                <Text copyable={{ text: subscriptionData.stripeCustomerId || '' }}>
                                                    {subscriptionData.stripeCustomerId || 'Not linked'}
                                                </Text>
                                            </Col>
                                            {subscriptionData.currentPeriodStart && (
                                                <Col xs={24} md={12}>
                                                    <div style={{ marginBottom: 8 }}>
                                                        <Text type="secondary">Current Period</Text>
                                                    </div>
                                                    <Text>
                                                        {dayjs(subscriptionData.currentPeriodStart).format('MMM DD, YYYY')} - {dayjs(subscriptionData.currentPeriodEnd).format('MMM DD, YYYY')}
                                                    </Text>
                                                </Col>
                                            )}
                                            {subscriptionData.stripeSubscriptionId && (
                                                <Col xs={24} md={12}>
                                                    <div style={{ marginBottom: 8 }}>
                                                        <Text type="secondary">Stripe Subscription ID</Text>
                                                    </div>
                                                    <Text copyable={{ text: subscriptionData.stripeSubscriptionId }}>
                                                        {subscriptionData.stripeSubscriptionId}
                                                    </Text>
                                                </Col>
                                            )}
                                        </Row>
                                    ) : (
                                        <Alert
                                            message="No Subscription"
                                            description="This organization does not have an active subscription."
                                            type="info"
                                            showIcon
                                        />
                                    )}
                                </Card>

                                {/* Purchased Add-ons */}
                                <Card
                                    title={
                                        <span>
                                            <ShoppingOutlined style={{ marginRight: 8 }} />
                                            Purchased Add-ons
                                        </span>
                                    }
                                >
                                    {addonsData && addonsData.length > 0 ? (
                                        <ResponsiveTable
                                            columns={[
                                                {
                                                    title: 'Add-on',
                                                    key: 'addon',
                                                    render: (_: any, record: any) => (
                                                        <div>
                                                            <div className="font-medium">{record.addon?.name || 'Unknown'}</div>
                                                            <div className="text-sm text-gray-500">{record.addon?.code}</div>
                                                        </div>
                                                    ),
                                                },
                                                {
                                                    title: 'Status',
                                                    dataIndex: 'status',
                                                    key: 'status',
                                                    render: (status: string) => {
                                                        const color = status === 'active' ? 'green' : status === 'expired' ? 'orange' : 'red';
                                                        const icon = status === 'active' ? <CheckCircleOutlined /> : <ClockCircleOutlined />;
                                                        return <Tag color={color} icon={icon}>{status?.toUpperCase()}</Tag>;
                                                    },
                                                },
                                                {
                                                    title: 'Type',
                                                    key: 'type',
                                                    render: (_: any, record: any) => (
                                                        <Tag color={record.addon?.addonType === 'recurring' ? 'purple' : 'blue'}>
                                                            {record.addon?.addonType?.toUpperCase() || 'N/A'}
                                                        </Tag>
                                                    ),
                                                },
                                                {
                                                    title: 'Purchased',
                                                    dataIndex: 'purchasedAt',
                                                    key: 'purchasedAt',
                                                    render: (date: string) => date ? dayjs(date).format('MMM DD, YYYY HH:mm') : 'N/A',
                                                },
                                                {
                                                    title: 'Expires',
                                                    dataIndex: 'expiresAt',
                                                    key: 'expiresAt',
                                                    render: (date: string) => date ? dayjs(date).format('MMM DD, YYYY') : 'Never',
                                                },
                                            ]}
                                            dataSource={addonsData}
                                            rowKey="id"
                                            pagination={false}
                                        />
                                    ) : (
                                        <Alert
                                            message="No Add-ons"
                                            description="This organization has not purchased any add-ons yet."
                                            type="info"
                                            showIcon
                                        />
                                    )}
                                </Card>
                            </>
                        )}
                    </TabPane>
                </Tabs>
            </Card>

            {/* Impersonation Modal */}
            <ImpersonationModal
                visible={isImpersonationModalVisible}
                onCancel={() => setIsImpersonationModalVisible(false)}
                organization={organization ? {
                    id: organization.id,
                    name: organization.name,
                    userCount: organization.userCount || 0,
                } : null}
            />

            {/* Status Update Modal */}
            <Modal
                title="Update Organization Status"
                open={isStatusModalVisible}
                onCancel={() => setIsStatusModalVisible(false)}
                onOk={() => statusForm.submit()}
                okText="Update"
            >
                <Form
                    form={statusForm}
                    onFinish={handleStatusUpdate}
                    layout="vertical"
                >
                    <Form.Item
                        name="status"
                        label="Status"
                        rules={[{ required: true, message: 'Please select a status' }]}
                    >
                        <Select>
                            <Select.Option value="ACTIVE">Active</Select.Option>
                            <Select.Option value="INACTIVE">Inactive</Select.Option>
                            <Select.Option value="SUSPENDED">Suspended</Select.Option>
                            <Select.Option value="PENDING">Pending</Select.Option>
                        </Select>
                    </Form.Item>
                </Form>
            </Modal>

            {/* Held trial email viewer — read the exact copy that was withheld,
                so a personal version can be written from it. */}
            <Modal
                title={
                    <span>
                        <MailOutlined style={{ marginRight: 8 }} />
                        Held email — never delivered
                    </span>
                }
                open={!!viewingHeldEmail}
                onCancel={() => setViewingHeldEmail(null)}
                footer={
                    <Button onClick={() => setViewingHeldEmail(null)}>Close</Button>
                }
                width={720}
            >
                {viewingHeldEmail && (
                    <>
                        <div style={{ marginBottom: 12 }}>
                            <Text type="secondary" style={{ fontSize: 12 }}>SUBJECT</Text>
                            <div><Text strong>{viewingHeldEmail.subject}</Text></div>
                        </div>
                        <div style={{ marginBottom: 16 }}>
                            <Text type="secondary" style={{ fontSize: 12 }}>WOULD HAVE BEEN SENT</Text>
                            <div>
                                <Text>
                                    {dayjs(
                                        viewingHeldEmail?.metadata?.wouldHaveSentAt ||
                                            viewingHeldEmail?.createdAt,
                                    ).format('MMM DD, YYYY HH:mm')}
                                </Text>
                            </div>
                        </div>
                        <div
                            style={{
                                border: '1px solid #f0f0f0',
                                borderRadius: 6,
                                padding: 16,
                                maxHeight: 420,
                                overflowY: 'auto',
                            }}
                            dangerouslySetInnerHTML={{ __html: sanitizeTrustedHtml(viewingHeldEmail.bodyHtml) }}
                        />
                    </>
                )}
            </Modal>

            {/* Benchmark Status Modal */}
            <Modal
                title={
                    <span>
                        <BarChartOutlined style={{ marginRight: 8 }} />
                        Set Benchmark Status
                    </span>
                }
                open={isBenchmarkModalVisible}
                onCancel={() => setIsBenchmarkModalVisible(false)}
                onOk={() => benchmarkForm.submit()}
                okText="Update"
                okButtonProps={{ loading: isUpdatingBenchmarkStatus }}
            >
                <Alert
                    message="Controls whether this organization's responses feed the shared benchmark pool. The org always sees benchmarks regardless. Changing the status triggers a full benchmark recalculation (takes a few minutes)."
                    type="info"
                    showIcon
                    style={{ marginBottom: 16 }}
                />
                <Form
                    form={benchmarkForm}
                    onFinish={handleBenchmarkStatusUpdate}
                    layout="vertical"
                >
                    <Form.Item
                        name="benchmarkStatus"
                        label="Benchmark status"
                        rules={[{ required: true, message: 'Please select a status' }]}
                    >
                        <Select>
                            <Select.Option value="pending">Pending — new signup, not yet reviewed (does not contribute)</Select.Option>
                            <Select.Option value="included">Included — verified real org, contributes to benchmarks</Select.Option>
                            <Select.Option value="excluded">Excluded — test/junk org, never contributes</Select.Option>
                        </Select>
                    </Form.Item>
                </Form>
            </Modal>

            {/* Assign to Agency Modal */}
            <Modal
                title={
                    <span>
                        <TeamOutlined style={{ marginRight: 8 }} />
                        Assign to Agency
                    </span>
                }
                open={isAgencyModalVisible}
                onCancel={() => {
                    setIsAgencyModalVisible(false);
                    agencyForm.resetFields();
                }}
                onOk={() => agencyForm.submit()}
                okText="Assign"
                okButtonProps={{ loading: isAssigningAgency }}
            >
                <Alert
                    message="The relationship becomes active immediately — no invitation is sent to the client organization."
                    type="info"
                    showIcon
                    style={{ marginBottom: 16 }}
                />
                <Form form={agencyForm} onFinish={handleAgencyAssign} layout="vertical">
                    <Form.Item
                        name="agencyOrganisationId"
                        label="Agency"
                        rules={[{ required: true, message: 'Please select an agency' }]}
                    >
                        <Select
                            showSearch
                            placeholder="Select an agency"
                            optionFilterProp="label"
                            options={(agencies || []).map((a: any) => ({
                                value: a.id,
                                label: `${a.name} (${a.clientCount} client${a.clientCount === 1 ? '' : 's'})`,
                            }))}
                        />
                    </Form.Item>
                </Form>
            </Modal>

            {/* Delete Organization Modal */}
            <Modal
                title={
                    <span style={{ color: '#ff4d4f' }}>
                        <ExclamationCircleOutlined style={{ marginRight: 8 }} />
                        Delete Organization - Danger Zone
                    </span>
                }
                open={isDeleteModalVisible}
                onCancel={() => {
                    setIsDeleteModalVisible(false);
                    setDeleteConfirmText('');
                }}
                onOk={handleDelete}
                okText="Delete Permanently"
                okButtonProps={{ danger: true, disabled: deleteConfirmText !== organization?.name, loading: isDeleting }}
                cancelButtonProps={{ disabled: isDeleting }}
            >
                <Alert
                    message="⚠️ WARNING: This action cannot be undone!"
                    description={
                        <div>
                            <p style={{ marginBottom: 16, fontWeight: 500 }}>
                                <strong>Will be permanently deleted:</strong>
                            </p>
                            <ul style={{ marginBottom: 16 }}>
                                <li><strong>{brandsData?.data?.length || 0}</strong> brands</li>
                                <li><strong>{surveysData?.data?.length || 0}</strong> surveys</li>
                                <li>All survey responses and analytics data</li>
                                <li>All questions, settings, and configurations</li>
                                <li>API keys and external metrics</li>
                            </ul>
                            <p style={{ marginBottom: 16, fontWeight: 500 }}>
                                <strong>Will be anonymized (kept for audit/compliance):</strong>
                            </p>
                            <ul style={{ marginBottom: 16 }}>
                                <li><strong>{usersData?.data?.length || 0}</strong> users (PII removed, marked as "Deleted User")</li>
                                <li>Organization record (renamed to "Deleted Organization #{organizationId}")</li>
                                <li>Event logs, AI conversations, and billing records remain linked</li>
                            </ul>
                        </div>
                    }
                    type="error"
                    showIcon
                    style={{ marginBottom: 24 }}
                />
                <div>
                    <Text strong>
                        To confirm, type <span style={{ color: '#d32f2f' }}>{organization?.name}</span> in the field below:
                    </Text>
                    <Input
                        placeholder={organization?.name}
                        value={deleteConfirmText}
                        onChange={(e) => setDeleteConfirmText(e.target.value)}
                        style={{ marginTop: 8 }}
                        disabled={isDeleting}
                    />
                </div>
            </Modal>
        </div>
    );
};